import { ImpulseElement, property, registerElement, target } from '@ambiki/impulse';
import axios from 'axios/index';
import useOutsideClick from '@ambiki/impulse-view-components/dist/hooks/use_outside_click';

/**
 *
 * This component allows the user to type and then click a search button to perform a search.
 * IT DOESN'T SEARCH ON A KEYSTROKE DELAY.
 *
 * After searching a select dropdown is provided to select the desired result. This is useful for
 * reducing API requests to an endpoint that may have a rate limit. (EG: NPI api)
 *
 */
@registerElement('search-select')
export default class SearchSelectElement extends ImpulseElement {
  /**
   * The endpoint to send the request too.
   */
  @property() src: string;

  /**
   * The endpoint's to send the request too.
   * Used when making a staged request.
   *
   */
  @property() srcs: Array<string>;

  /**
   * This is the type of control. Please pass a type and update the sections below
   * were relevant.
   */
  @property() type: string;

  /**
   * The HTML select element.
   */
  @target() select: HTMLSelectElement;

  /**
   * The HTML div element.
   */
  @target() dropdown: HTMLElement;

  /**
   * The HTML div element.
   */
  @target() clickContainer: HTMLElement;

  /**
   * The HTML hidden input element containing the result.
   */
  @target() valueContainer: HTMLInputElement;

  /**
   * NPI search specific additional fields.
   *
   */
  @target() firstName: HTMLInputElement;
  @target() lastName: HTMLInputElement;
  @target() middleName: HTMLInputElement;

  /**
   * Stores the current select name value while the user is typing to search.
   * @private
   */
  private storeValue: string;

  /**
   * Used in a staged request to know when they are all complete.
   * @private
   */
  private requestCompletionCounter = 0;

  /**
   * Used to store and failed request when performing a staged request.
   * Assists in error messaging.
   * @private
   */
  private hasResults = false;

  connected() {
    // Store the value of the select item if present on load.
    this.storeValue = this.select.value;
    this.closeOnOutsideClickEvent();
  }

  /**
   *
   *  Adds a click event listener to the outside field of our element.
   *  If the dropdown is open we will preform certain actions.
   *
   * @function closeOnOutsideClickEvent
   * @return null
   */
  closeOnOutsideClickEvent() {
    useOutsideClick(this, {
      boundaries: [this.clickContainer],
      callback: () => {
        this.select.value = this.storeValue;
        this.removeError();
        this.toggleDropdown('close');
      },
    });
  }

  /**
   *
   *  Performs a single API request and populates our select field, adds relevant
   *  event listeners and UI functionality.
   *
   * @function makeRequest
   * @return nil
   */
  async makeRequest() {
    this.dropdown.innerHTML = '';
    this.removeError();
    this.addSpinner();
    this.toggleDropdown('open');

    try {
      const { data } = await axios.get(this.src, { params: { q: this.select.value } });
      if (data.length === 0) {
        this.errorMessage('No results found.');
      }

      data.forEach((result: Record<string, unknown>) => {
        const option = document.createElement('div');
        option.className = 'option';
        option.textContent = this.textFormat(result);

        const activeOption = this.typeValueMatcher(result);
        if (activeOption) option.classList.add('selected');

        option.addEventListener('click', () => {
          this.selectItem(result);
          this.toggleDropdown('close');
        });
        this.removeSpinner();
        this.dropdown.appendChild(option);
      });
    } catch (error) {
      this.errorMessage('Something went wrong, please try again.');
    }
  }

  /**
   *
   *  Used when calling multiple APIs at the same time to increase performance
   *  when one API call is slow.
   *
   * @function stagedRequest
   * @return nil
   */
  async stagedRequest() {
    this.dropdown.innerHTML = '';
    this.requestCompletionCounter = 0;
    this.hasResults = false;
    this.removeError();
    this.addSpinner();
    this.toggleDropdown('open');

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    for (const url of JSON.parse(this.srcs)) {
      // We are intentionally not waiting for the requests for performance reasons.
      // eslint-disable-next-line no-await-in-loop
      this.multiRequest(url.toString());
    }
  }

  /**
   *
   *  Performs the API call for a stagedRequest. Has additional functionality regarding
   *  grouping of results and UI functionality.
   *
   * @param url
   * @private
   */
  private async multiRequest(url: string) {
    try {
      const { data } = await axios.get(url, { params: { q: this.select.value } });
      if (data.length > 0) {
        this.hasResults = true;
      }

      data.forEach((result: Record<string, unknown>) => {
        const option = document.createElement('div');
        option.className = 'option';
        option.textContent = this.textFormat(result);

        const activeOption = this.typeValueMatcher(result);
        if (activeOption) option.classList.add('selected');

        option.addEventListener('click', () => {
          this.selectItem(result);
          this.toggleDropdown('close');
        });

        this.dropdown.appendChild(option);
      });

      this.requestCompletionCounter += 1;
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      if (this.requestCompletionCounter === JSON.parse(this.srcs).length) {
        this.removeSpinner();
        if (!this.hasResults) {
          this.errorMessage('No results found.');
        }
        this.hasResults = false;
      } else {
        this.removeSpinner();
        this.addSpinner();
      }
    } catch (error) {
      this.errorMessage('Something went wrong, please try again.');
    }
  }

  /**
   *
   *  Sets the relevant item details when an option is selected. This is dependent
   *  on the type property set above.
   *
   * @function selectItem
   * @param result - a single item returned from the API call
   * @private
   */
  private selectItem(result: Record<string, unknown>) {
    if (this.type === 'npi') {
      this.select.value = `${result.first_name} ${result.last_name} (${result.npi_number})`;
      this.storeValue = `${result.first_name} ${result.last_name} (${result.npi_number})`;
      this.valueContainer.value = `${result.npi_number}`;

      this.firstName.value = `${result.first_name}`;
      this.lastName.value = `${result.last_name}`;
      this.middleName.value = `${result.middle_name}`;
    }
  }

  /**
   *
   *  Formats the text for the input field. Depends on the type value set above.
   *
   * @function textFormat
   * @param result - a single item returned from the API call
   * @private
   */
  private textFormat(result: Record<string, unknown>) {
    if (this.type === 'npi') {
      return `${result.first_name} ${result.last_name} (${result.npi_number})`;
    }
    return `${result.name}`;
  }

  /**
   *
   *  Check's if an option from the API is the current active option.
   *  Depends on the type property set above.
   *
   * @function typeValueMatcher
   * @param result - a single item returned from the API call
   * @private
   */
  private typeValueMatcher(result: Record<string, unknown>) {
    let activeOption = false;
    if (this.type === 'npi') {
      if (result.npi_number === this.valueContainer.value) activeOption = true;
    }
    return activeOption;
  }

  /**
   *
   *  Toggles the visibility of the select dropdown.
   *
   * @function toggleDropdown
   * @param type - Either 'open' of 'closed', state of the element.
   * @private
   */
  private toggleDropdown(type: string) {
    if (type === 'open') {
      this.dropdown.classList.remove('d-none');
      this.dropdown.classList.add('search-select-dropdown-visible');
    } else {
      this.dropdown.classList.remove('d-none');
      this.dropdown.classList.add('d-none');
      this.dropdown.classList.remove('search-select-dropdown-visible');
    }
  }

  /**
   *
   *  Creates and adds the loading spinner element to the dropdown menu.
   *
   * @function addSpinner
   * @private
   */
  private addSpinner() {
    const spinner = document.createElement('span');
    spinner.className = 'spinner-border spinner-border-lg gray-500-text my-3';
    spinner.id = 'temp-spinner';
    this.dropdown.classList.add('row', 'justify-content-center', 'align-content-center');
    this.dropdown.appendChild(spinner);
  }

  /**
   *
   *  Removes the loading spinner element to the dropdown menu once the API request has
   *  completed.
   *
   * @function removeSpinner
   * @private
   */
  private removeSpinner() {
    const spinner = document.getElementById('temp-spinner');
    if (spinner) spinner.remove();
    this.dropdown.classList.remove('row', 'justify-content-center', 'align-content-center');
  }

  /**
   *
   * Will display an error message in the dropdown.
   *
   * @param message The error message
   * @private
   */
  private errorMessage(message: string) {
    const error = document.createElement('p');
    error.className = 'text-danger my-3 col-12 text-center';
    error.id = 'temp-error';
    error.textContent = message;
    this.dropdown.classList.add('row', 'justify-content-center', 'align-content-center');
    this.dropdown.appendChild(error);
  }

  /**
   *
   *  Removes the error message from the dropdown menu.
   *
   * @function removeError
   * @private
   */
  private removeError() {
    const error = document.getElementById('temp-error');
    if (error) error.remove();
    this.dropdown.classList.remove('row', 'justify-content-center', 'align-content-center');
  }
}
